import { Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  readonly currentLang = signal<string>('en');
  private readonly supportedLanguages = ['en', 'fr', 'ar'] as const;

  constructor(private readonly translateService: TranslateService) {}

  initLanguage(): void {
    const stored = this.safeStorageGet('shl-lang');
    const storedLang = this.normalizeLang(stored);
    if (storedLang && this.isSupported(storedLang)) {
      this.setLanguage(storedLang);
      return;
    }

    const browserLang = this.normalizeLang(typeof navigator !== 'undefined' ? navigator.language : '');
    const finalLang = browserLang && this.isSupported(browserLang) ? browserLang : 'en';
    this.setLanguage(finalLang);
  }

  setLanguage(lang: string): void {
    const nextLang = this.isSupported(lang) ? lang : 'en';
    this.currentLang.set(nextLang);
    this.safeStorageSet('shl-lang', nextLang);

    const root = document.documentElement;
    root.setAttribute('lang', nextLang);
    root.setAttribute('dir', nextLang === 'ar' ? 'rtl' : 'ltr');

    this.translateService.use(nextLang);
  }

  private normalizeLang(value?: string | null): string {
    if (!value) {
      return '';
    }
    return value.toLowerCase().split('-')[0].split('_')[0];
  }

  private isSupported(lang: string): lang is (typeof this.supportedLanguages)[number] {
    return this.supportedLanguages.includes(lang as (typeof this.supportedLanguages)[number]);
  }

  private safeStorageGet(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private safeStorageSet(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // no-op for environments without storage
    }
  }
}
